// Манометры фичи Steam Spin: три стрелки на циферблатах между бонусным рядом и полем.
//
// Циферблаты нарисованы прямо на картинке машины, по одному на колонку барабанов,
// см. GDD, раздел 5.1. Игра ставит на них стрелки и крутит их по заряду манометра.
//
// Скелет стрелки ходит вперёд с деления на соседнее. Прыжок через несколько делений играется
// цепочкой: стрелка проходит их подряд, как настоящая. Заряд манометра по правилам игры только
// растёт, см. GDD, раздел 4.2, поэтому ходов назад в ассете нет ни одного, кроме возврата
// с потолка в ноль. Им и кончается бонус Steam Spin, см. GDD, раздел 5.4.
//
// Координаты здесь те же, что у барабанов: это пиксели картинки барабанов.

import { Container } from 'pixi.js';

import type { GaugeStep } from '../../api/spin';
import { arrowIdleAnimation, arrowResetAnimation, arrowStepAnimation } from '../../assets/catalog';
import type { AssetManager, Spine, TrackEntry } from '../../assets/manager';
import { gaugeArrow } from '../../ui/machine';
import { animationSeconds } from '../../ui/scene';

/**
 * Ходы стрелки от одного значения к другому.
 *
 * Стрелка идёт делениями вверх, поэтому путь — это цепочка ходов. Путь на месте пуст:
 * стойка на этом значении уже играет, и заново её пускать незачем.
 *
 * Ходов вниз у скелета нет, и по правилам игры они не нужны: заряд манометра только растёт.
 * Упавшее значение стрелка проходит без хода, одной стойкой.
 *
 * @param from Значение, на котором стрелка стоит сейчас.
 * @param to Значение, на которое ей идти.
 * @param max Потолок манометра. По GDD, раздел 5.2, это 10.
 */
export function arrowSteps(from: number, to: number, max: number): string[] {
  const start: number = clamp(from, max);
  const target: number = clamp(to, max);

  const steps: string[] = [];
  for (let value = start + 1; value <= target; value++)
    steps.push(arrowStepAnimation(value));

  return steps;
}

/** Приводит значение к шкале манометра. */
function clamp(value: number, max: number): number {
  return Math.max(0, Math.min(Math.round(value), max));
}

/**
 * Куда встанет каждая стрелка по шагам спина.
 *
 * Один спин заряжает манометр столько раз, сколько множителей выпало на его барабане.
 * Стрелке от этого ход один: она идёт делениями от нынешнего значения до последнего,
 * и промежуточные шаги видны сами собой.
 */
export function gaugeTargets(steps: readonly GaugeStep[]): Map<number, number> {
  const targets = new Map<number, number>();

  for (const step of steps)
    targets.set(step.reel, step.to);

  return targets;
}

/** Три манометра над полем: каждый показывает заряд своего барабана. */
export class Gauges {
  readonly view: Container = new Container();

  readonly #arrows: Spine[];
  readonly #max: number;
  readonly #values: number[];

  /** Кого разбудить, когда стрелки доедут. Показ манометров ждёт их все. */
  readonly #waiting: (() => void)[] = [];

  /**
   * @param reels Сколько манометров, по одному на барабан.
   * @param max Потолок манометра.
   * @throws Error если на картинке машины нарисовано меньше циферблатов, чем нужно манометров
   */
  constructor(assets: AssetManager, reels: number, max: number) {
    this.#max = max;
    this.#values = new Array<number>(reels).fill(0);

    this.#arrows = [];

    for (let column = 0; column < reels; column++) {
      const arrow: Spine = gaugeArrow(assets, column);

      this.#arrows.push(arrow);
      this.view.addChild(arrow);
    }
  }

  /** Заряд каждого манометра на этот миг. По нему мини-слот ставит свои множители. */
  get values(): readonly number[] {
    return this.#values;
  }

  /**
   * Ставит стрелки на заданные значения без хода.
   *
   * Так игра показывает состояние сессии на загрузке: заряд манометра набран прошлыми
   * раундами, и крутить его на глазах у игрока нечего.
   */
  show(values: readonly number[]): void {
    this.#arrows.forEach((arrow: Spine, column: number) => {
      const value: number = clamp(values[column] ?? this.#values[column], this.#max);

      this.#values[column] = value;
      arrow.state.setAnimation(0, arrowIdleAnimation(value), true);
    });

    this.#wake();
  }

  /**
   * Крутит стрелки по шагам спина и ждёт, пока они доедут.
   *
   * Стрелки идут разом: манометры разных барабанов заряжаются одним спином, и ждать друг
   * друга им незачем. Ожидание кончается по самой долгой из них.
   *
   * Несколько шагов одного манометра сводятся в один ход: вторая анимация на той же дорожке
   * оборвала бы первую, и ожидание первой не кончилось бы никогда.
   */
  charge(steps: readonly GaugeStep[]): Promise<void> {
    const turned: Promise<void>[] = [];

    for (const [reel, value] of gaugeTargets(steps))
      turned.push(this.#turn(reel, value));

    return Promise.all(turned).then(() => undefined);
  }

  /**
   * Сбрасывает манометры в ноль и ждёт, пока стрелки вернутся.
   *
   * Так кончается бонус Steam Spin, см. GDD, раздел 5.4. Ход назад в скелете один и нарисован
   * с потолка шкалы, поэтому его играет только манометр, дошедший до потолка. Остальные встают
   * в ноль без хода: ходов вниз с прочих делений в ассете нет.
   *
   * Потолок манометра задаёт описание игры, а ход нарисован под десятку. Поэтому потолок,
   * под который хода нет, стрелка тоже проходит без хода, а не роняет игру.
   */
  reset(): Promise<void> {
    const back: string = arrowResetAnimation(this.#max);
    const returned: Promise<void>[] = [];

    this.#arrows.forEach((arrow: Spine, column: number) => {
      const turns: boolean = this.#values[column] >= this.#max && animationSeconds(arrow, back) > 0;

      this.#values[column] = 0;

      if (!turns) {
        arrow.state.setAnimation(0, arrowIdleAnimation(0), true);
        return;
      }

      arrow.state.setAnimation(0, back, false);
      returned.push(this.#await(arrow.state.addAnimation(0, arrowIdleAnimation(0), true, 0)));
    });

    return Promise.all(returned).then(() => undefined);
  }

  /** Ставит стрелки на их значения разом: показ манометров пропущен. */
  skip(): void {
    this.show(this.#values);
  }

  /** Крутит одну стрелку до значения и ждёт, пока она доедет. */
  #turn(column: number, value: number): Promise<void> {
    const arrow: Spine | undefined = this.#arrows[column];
    if (arrow === undefined)
      return Promise.resolve();

    const steps: string[] = arrowSteps(this.#values[column], value, this.#max);
    const target: number = clamp(value, this.#max);

    this.#values[column] = target;

    if (steps.length === 0) {
      arrow.state.setAnimation(0, arrowIdleAnimation(target), true);
      return Promise.resolve();
    }

    // Ходы ставятся в очередь дорожки: Spine играет их подряд, а стойка держит стрелку
    // на месте, пока манометр не двинется снова.
    arrow.state.setAnimation(0, steps[0], false);

    for (const step of steps.slice(1))
      arrow.state.addAnimation(0, step, false, 0);

    return this.#await(arrow.state.addAnimation(0, arrowIdleAnimation(target), true, 0));
  }

  /** Ждёт, пока дорожка дойдёт до заданной анимации. */
  #await(last: TrackEntry): Promise<void> {
    return new Promise<void>((resolve) => {
      // Ожидание кончает либо сама стрелка, либо скип: брошенное ожидание не кончилось бы
      // никогда, и раунд встал бы на нём.
      const done = (): void => {
        const at: number = this.#waiting.indexOf(done);
        if (at < 0)
          return;

        this.#waiting.splice(at, 1);
        resolve();
      };

      this.#waiting.push(done);
      last.listener = { start: done };
    });
  }

  /** Будит всех, кто ждёт стрелок. */
  #wake(): void {
    for (const done of [...this.#waiting])
      done();
  }
}
